import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../lib/api';
import { useWebSocket, type NotificationMessage } from './useWebSocket';

export interface ActiveJob {
  id: string;
  status: 'queued' | 'processing' | 'completed' | 'retrying' | 'permanently_failed' | 'failed';
  prompt: string;
  image_size: string;
  quality_level: string;
  num_variants: number;
  credit_cost: number;
  created_at: string;
  updated_at?: string;
  thumbnail_url?: string;
  error_message?: string;
}

export function useActiveJobs() {
  const [jobs, setJobs] = useState<ActiveJob[]>([]);
  const [loading, setLoading] = useState(true);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await api.get<{ items: ActiveJob[] }>('/generations', { page_size: '50' });
      setJobs(res.data?.items ?? []);
    } catch {
      // Silently fail polling
    } finally {
      setLoading(false);
    }
  }, []);

  const handleWsMessage = useCallback((msg: NotificationMessage) => {
    setJobs((prev) => {
      const idx = prev.findIndex((j) => j.id === msg.job_id);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = {
          ...updated[idx],
          status: msg.status,
          thumbnail_url: msg.thumbnail_url || updated[idx].thumbnail_url,
          error_message: msg.error_message,
        };
        return updated;
      }
      // New job appeared (submitted from this page)
      return prev;
    });
  }, []);

  useWebSocket({
    onMessage: handleWsMessage,
    enabled: true,
  });

  // Initial fetch
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Always poll every 3 seconds for updates
  useEffect(() => {
    pollIntervalRef.current = setInterval(fetchJobs, 3000);
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchJobs]);

  return { jobs, loading, refetch: fetchJobs };
}
